import React, { ReactElement, useRef, useState, useEffect, useMemo } from 'react'
import type { Dispatch } from 'redux'
import { useDispatch, useSelector } from 'react-redux'
import { MenuProps, Popconfirm } from 'antd'
import { Image, Menu, Button, Upload, Popover, Form, Input, Typography, Modal } from 'antd'
import {
    BlockOutlined, SettingOutlined, AppstoreOutlined, ReadOutlined, UploadOutlined, UserSwitchOutlined,
    DeleteOutlined, ExclamationCircleOutlined, PlayCircleOutlined
} from '@ant-design/icons'
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface'
import { signin, signup } from '@/store/actions/auth.action'
import { requestFail } from '@/store/actions/common.action'
import { getErrorStr } from '@/store/sagas/table.saga'
import { IAppState } from '@/store/reducers'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import Logo from '@/assets/images/logo.png'
import { uploadFile, getFileList, delFile } from '@/services/progress.service'
import { exportExcel } from '@/services/table.service'
import { UploadAPI } from '@/config'
import { autoClick, downloadFile } from '@/utils/util'
import { validateAuth } from '@/utils/auth'

type MenuItem = Required<MenuProps>['items'][number];
export interface IWorkgroupRouteParam {
    text: string
    url: string
    data?: number
}

function getItem(
    label: React.ReactNode,
    key: React.Key,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group',
): MenuItem {
    return {
        key,
        icon,
        children,
        label,
        type,
        popupOffset: [-20, 0]
    } as MenuItem
}

const SimulationLogin = (): ReactElement => {
    const dispatch = useDispatch()
    const { t, i18n } = useTranslation()
    const onFinish = async (value: { username: string }) => {
        dispatch(signin({
            ...value,
            message: t('login') + (i18n.language !== 'zh' ? ' ' : '') + t('success'),
            simulation: true
        }))
    }
    return (
        <Form
            name="basic"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={{ remember: true }}
            autoComplete="off"
            onFinish={onFinish}
        >
            <Form.Item
                label={t('uidq')}
                name="username"
                rules={[{ required: true, message: t('userPswMsg') }]}
            >
                <Input />
            </Form.Item>

            <Form.Item wrapperCol={{ offset: 8, span: 16 }} style={{ marginBottom: 0 }}>
                <Button type="primary" htmlType="submit">{t('submit')}</Button>
            </Form.Item>
        </Form>
    )
}

// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
function Nav({ dispatch }: {
    dispatch: Dispatch
    showList?: IWorkgroupRouteParam[]
    setShowList?: React.Dispatch<React.SetStateAction<IWorkgroupRouteParam[]>>
}): ReactElement {
    const history = useHistory()
    const location = useLocation()
    const { t, i18n } = useTranslation()
    const userGuide = useRef('userGuide')

    const { Text } = Typography

    const authInfo = useSelector((state: IAppState) => state.auth.authInfo)
    const [fileList, setFileList] = useState<(UploadFile & {
        url?: string
        fileId?: number
        status?: string
    })[]>([])
    const [selectedKeys, setSelectedKeys] = useState<string[]>([])

    const validateAuthByKey = (key: string) => validateAuth(authInfo, key)
    // 封装拼接字符串的函数
    const joinTranslatedStrings = (...translations: string[]) => {
        return translations.join(i18n.language !== 'zh' ? ' ' : '')
    }

    const menuItems: MenuItem[] = useMemo(() => [
        getItem(t('indexPage'), '/', <BlockOutlined />),
        getItem(t('technicalReview'), '2', <SettingOutlined />, [
            ...(validateAuthByKey('reviewStar') ? [getItem(joinTranslatedStrings(t('add'), t('technicalReview'), t('apply')), '/techReviewAdd?type=1', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'techReviewProgress') ? [getItem(t('technicalReviewProcess'), '/techReviewProgress', <AppstoreOutlined />)] : []),
            // ...(authInfo && Reflect.has(authInfo, 'techChangeList') ? [getItem(t('Tech Change Status') + (i18n.language !== 'zh' ? ' ' : '') + t('list'), '/techChangeList', <AppstoreOutlined />)] : []),
        ]),
        getItem(t('technologyDomains'), '3', <SettingOutlined />, [
            ...(authInfo && Reflect.has(authInfo, 'globleSearch') ? [getItem(t('Globle Search'), '/projectInfoSearch', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'exportAll') ? [getItem(t('exportAll'), '/exportAll', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'techTree') ? [getItem(t('techTree'), '/techTree', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'changeApplyList') ? [getItem(t('approvalList'), '/projectInfoChangeApplyList', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'technicalShelf') ? [getItem(t('technicalShelf'), '/technicalShelf', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'viewLogListAll') ? [getItem(t('View Log List All'), '/viewLogList', <AppstoreOutlined />)] : []),
        ]),
        getItem(t('systemFunc'), '4', <SettingOutlined />, [
            getItem(t('TMTTechBack'), '/voteReport', <AppstoreOutlined />),
            ...(authInfo && Reflect.has(authInfo, 'manageWorkgroup') ? [getItem(t('manage workgroup'), '/workgroupAdd', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'organStructure') ? [getItem(t('organStructure'), '/organList', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'contributionDegree') ? [getItem(t('contribution degree'), '/contribution', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'buAndCtListMana') ? [getItem(t('buAndCtList') + (i18n.language !== 'zh' ? ' ' : '') + t('manage'), '/buAndCtListMana', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'prodListMana') ? [getItem(t('product') + (i18n.language !== 'zh' ? ' ' : '') + t('manage'), '/prodListMana', <AppstoreOutlined />)] : []),
            ...(authInfo && Reflect.has(authInfo, 'techRelative') ? [getItem(t('Tech Relative'), '/techRelativeAnalysis', <AppstoreOutlined />)] : []),
            ...(validateAuthByKey('contributionDegree') ? [getItem(t('proposal board'), '/proposalBoard', <AppstoreOutlined />)] : []),
        ]),
        getItem(t('toolManagement'), '5', <SettingOutlined />, [
            getItem(t('toolManagement'), '/toolIndex', <AppstoreOutlined />),
        ]),
    ], [authInfo])
    const showUserGuide = async (): Promise<void> => {
        const { content } = await getFileList(userGuide.current)
        if (content.length) {
            autoClick(`${UploadAPI}/onlinePreview?filePath=${content[0].filePath}`)
        }
    }
    const showUserGuideVideo = async (): Promise<void> => {
        autoClick('https://yesv-desaysv.feishu.cn/file/Lt3mbFVuWomZXExkag9c7Qwpnoc')
    }

    const props: UploadProps = {
        fileList,
        showUploadList: {
            removeIcon: <Text className='txt_red hand' title={t('delete')}><DeleteOutlined style={{ fontSize: 16 }} /></Text>
        },
        beforeUpload() {
            return false
        },
        onChange: async info => {
            console.log('onChange: ', info)
            if (info.fileList.length < 1) {
                return
            }
            try {
                setFileList([{
                    ...info.file,
                    status: 'uploading'
                }])
                const formData = new FormData()
                formData.append('multipartFile', info.file as RcFile)
                const res = await uploadFile(formData, userGuide.current)
                console.log('uploadFile: ', res)
                setFileList([{
                    ...info.file,
                    fileId: res[0]?.id,
                    name: res[0]?.fileName,
                    url: `${UploadAPI}/onlinePreview?filePath=${res[0]?.fullPath}`,
                    status: 'success'
                }])
            } catch (error: any) {
                const str = getErrorStr(error)
                dispatch(requestFail(str))
            }
        },
        onRemove: async (file: UploadFile & {
            url?: string
            fileId?: number
            status?: string
        }) => {
            try {
                file.status = 'removed'
                setFileList([{
                    ...file
                }])
                await delFile(file.fileId as number)
                setFileList([])
                return Promise.resolve()
            } catch (error: any) {
                file.status = 'error'
                setFileList([{
                    ...file
                }])
                const str = getErrorStr(error)
                dispatch(requestFail(str))
                return Promise.reject(error)
            }
        }
    }

    const onClickMenu: MenuProps['onClick'] = ({ key }) => {
        if (key === '/toolIndex') {
            autoClick('http://devops.desaysv.com/#/tools/tool')
            return
        } else if (key === '/exportAll') {
            const exportFn = async () => {
                const res = await exportExcel({})
                downloadFile(res, 'All' + '.ProjectInfo.' + res.suffix)
            }
            Modal.confirm({
                title: t('confirmDownload'),
                okText: t('confirm'),
                cancelText: t('cancel'),
                icon: <ExclamationCircleOutlined />,
                onOk() {
                    return exportFn()
                },
                onCancel() {
                    console.log('Cancel')
                },
            })
            return
        }
        setSelectedKeys([key])
        history.push(key)
    }

    useEffect(() => {
        if (location.pathname === '/techReviewAdd') {
            setSelectedKeys(['/techReviewAdd?type=1'])
        } else {
            setSelectedKeys([location.pathname])
        }
    }, [])

    return <div className='navgate'>
        <Image src={Logo} height={46} preview={false} style={{ cursor: 'pointer' }}
            onClick={() => {
                const key = '/'
                setSelectedKeys([key])
                history.push(key)
            }}
        />
        <Menu mode="horizontal"
            theme="dark"
            style={{ marginLeft: 20, flex: 1 }}
            items={menuItems}
            selectedKeys={selectedKeys}
            onClick={onClickMenu}
        />
        <div style={{ marginRight: 24 }}>
            <Button key="1" type='text'
                style={{ paddingRight: 8, color: '#fff' }}
                onClick={() => {
                    const lang = i18n.language == 'en' ? 'zh' : 'en'
                    i18n.changeLanguage(lang)
                    localStorage.setItem('lang', lang.substring(0, 2))
                    // if (showList && setShowList) {
                    //     const _showList = showList.slice(0)
                    //     _showList.splice(0, 1, {
                    //         text: `${t('charts')}${i18n.language !== 'zh' ? ' ' : ''}${t('list')}`,
                    //         url: '/charts'
                    //     })
                    //     _showList.splice(1, 1, {
                    //         text: t('manage') + (i18n.language !== 'zh' ? ' ' : '') + t('technology roadmap'),
                    //         url: '/keyRoadMap'
                    //     })
                    //     console.log('_showList: ', _showList)
                    //     setShowList(_showList)
                    // }
                }}
            >
                {i18n.language == 'en' ? '中文' : 'EN'}
            </Button>
            <Button title={t('userGuide')}
                type='text'
                style={{ display: 'inline-block', verticalAlign: 'middle', paddingLeft: 8, paddingRight: 8 }}
                onClick={showUserGuide}
            ><ReadOutlined style={{ fontSize: 18, color: '#fff' }} /></Button>
            <Button title={t('userGuide')}
                type='text'
                style={{ display: 'inline-block', verticalAlign: 'middle', paddingLeft: 8 }}
                onClick={showUserGuideVideo}
            ><PlayCircleOutlined style={{ fontSize: 18, color: '#fff' }} /></Button>
            {authInfo && Reflect.has(authInfo, 'uploadUserDoc') &&
                <div style={{ display: 'inline-block', marginRight: 15 }}>
                    <Upload {...props}>
                        <Button icon={<UploadOutlined />}>{t('uploadUserGuide')}</Button>
                    </Upload>
                </div>}
            {/* 登出 */}
            <Popconfirm
                title={joinTranslatedStrings(t('confirm'),t('logout'))}
                onConfirm={() => {
                    dispatch(signup(t('logout') + (i18n.language !== 'zh' ? ' ' : '') + t('success')))
                }}
                okText={t('confirm')}
                cancelText={t('cancel')}
            >
                <Button>{t('logout')}</Button>
            </Popconfirm>
            {authInfo && Reflect.has(authInfo, 'simulationLogin') && <Popover content={<SimulationLogin />} title={t('simulationLogin')} placement="bottomRight"
                trigger="click"
                overlayClassName="simulation_login"
            >
                <Button type='text' title={t('simulationLogin')}><UserSwitchOutlined style={{ fontSize: 18, color: '#fff' }} /></Button>
            </Popover>}
        </div>
    </div>
}

export default Nav
